import { useState, type ReactNode } from "react";
import { BADGE_TONES, type Tone } from "../ui/Badge";
import { cn } from "../ui/cn";
import { useResolvedUnitIcon, type UnitIconSource } from "./UnitIcon";

export const UNIT_AVATAR_SIZES = {
  sm: { tile: "h-9 w-9 rounded-lg text-base", image: "h-9 w-9 rounded-md" },
  md: { tile: "h-10 w-10 rounded-xl text-lg", image: "h-10 w-10 rounded-lg" },
  lg: { tile: "h-12 w-12 rounded-xl text-xl", image: "h-12 w-12 rounded-lg" },
} as const satisfies Record<string, { tile: string; image: string }>;

export type UnitAvatarSize = keyof typeof UNIT_AVATAR_SIZES;

export interface UnitAvatarProps extends UnitIconSource {
  iconId?: string | null;
  name?: string;
  size?: UnitAvatarSize;
  tone?: Tone;
  /** Clases del recuadro de respaldo, cuando el tono no aplica. */
  toneClassName?: string;
  fallback?: ReactNode;
  photoFallback?: boolean;
  className?: string;
}

/**
 * Recuadro de 40 px con el icono de la unidad. Con icono del catálogo pinta la
 * imagen recortada dentro de un borde y sin relleno de color: el fondo tintado
 * es del respaldo, y sobre una foto solo la ensucia.
 */
export default function UnitAvatar({
  iconId,
  icon,
  type,
  model,
  brand,
  name,
  size = "md",
  tone = "neutral",
  toneClassName,
  fallback = <i className="ri-truck-line" />,
  photoFallback = false,
  className,
}: UnitAvatarProps) {
  const [failed, setFailed] = useState(false);
  const src = useResolvedUnitIcon({ iconId, icon, type, model, brand }, photoFallback);
  const scale = UNIT_AVATAR_SIZES[size];

  if (!src || failed) {
    return (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center",
          scale.tile,
          toneClassName ?? BADGE_TONES[tone].soft,
          className,
        )}
      >
        {fallback}
      </span>
    );
  }

  return (
    <span className={cn("flex shrink-0 items-center justify-center", scale.tile, className)}>
      <img
        src={src}
        alt={name ?? ""}
        onError={() => setFailed(true)}
        className={cn("border border-line bg-app object-contain p-px", scale.image)}
      />
    </span>
  );
}
